(function () {
  'use strict';

  var GLOBAL_KEY = 'HeroScrollSequence';

  if (!window[GLOBAL_KEY]) {
    class HeroScrollSequence {
      constructor(section) {
        this.section = section;
        this.canvas = section.querySelector('[data-hero-scroll-canvas]');
        this.context = this.canvas ? this.canvas.getContext('2d', { alpha: false }) : null;
        this.loader = section.querySelector('[data-hero-scroll-loader]');
        this.loaderText = section.querySelector('[data-hero-scroll-loader-text]');

        this.totalFrames = this.clampInt(section.dataset.totalFrames, 1, 9999, 300);
        this.prefix = section.dataset.framePrefix || 'frame_';
        this.extension = (section.dataset.frameExtension || 'webp').replace(/^\./, '');
        this.firstFrameUrl = section.dataset.firstFrameUrl || '';
        this.videoUrl = section.dataset.videoUrl || '';
        this.sourceMode = this.videoUrl ? 'video' : 'sequence';
        this.video = null;
        this.videoDuration = 0;
        this.targetTime = 0;
        this.drawnVideoTime = -1;
        this.frames = new Array(this.totalFrames);
        this.loadedFrames = new Set();
        this.settledCount = 0;
        this.currentFrame = 0;
        this.targetFrame = 0;
        this.drawnFrame = -1;
        this.animationFrame = 0;
        this.scrollFrame = 0;
        this.sectionTop = 0;
        this.scrollDistance = 1;
        this.isActive = true;
        this.isDestroyed = false;
        this.isReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.minimumReady = this.isReducedMotion
          ? 1
          : Math.min(this.totalFrames, Math.max(8, Math.ceil(this.totalFrames * 0.08)));

        this.onScroll = this.onScroll.bind(this);
        this.onResize = this.onResize.bind(this);
        this.tick = this.tick.bind(this);

        if (!this.canvas || !this.context || (this.sourceMode === 'sequence' && !this.firstFrameUrl)) {
          this.showError();
          return;
        }

        this.configureFromSettings();
        this.bindEvents();
        this.resizeCanvas();
        this.measure();
        if (this.sourceMode === 'video') {
          this.initializeVideo();
        } else {
          this.preloadSequence();
        }
      }

      clampInt(value, minimum, maximum, fallback) {
        var parsed = parseInt(value, 10);
        if (!Number.isFinite(parsed)) return fallback;
        return Math.min(maximum, Math.max(minimum, parsed));
      }

      configureFromSettings() {
        var heroHeight = this.clampInt(this.section.dataset.heroHeight, 70, 120, 100);
        var scrollLength = this.clampInt(this.section.dataset.scrollLength, 200, 1000, 600);
        var background = this.section.dataset.backgroundColor || '#e4ebdd';

        this.section.style.setProperty('--hero-scroll-height', heroHeight + 'vh');
        this.section.style.setProperty('--hero-scroll-length', scrollLength + 'vh');
        this.section.style.setProperty('--hero-scroll-background', background);
      }

      bindEvents() {
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onResize, { passive: true });

        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(this.onResize);
          this.resizeObserver.observe(this.section);
        }

        if ('IntersectionObserver' in window) {
          this.visibilityObserver = new IntersectionObserver((entries) => {
            this.isActive = Boolean(entries[0] && entries[0].isIntersecting);
            if (this.isActive) this.onScroll();
          }, { rootMargin: '100% 0px' });
          this.visibilityObserver.observe(this.section);
        }
      }

      makeFrameUrl(frameNumber) {
        var filename = this.prefix + String(frameNumber).padStart(4, '0') + '.' + this.extension;
        var queryIndex = this.firstFrameUrl.indexOf('?');
        var base = queryIndex === -1 ? this.firstFrameUrl : this.firstFrameUrl.slice(0, queryIndex);
        var query = queryIndex === -1 ? '' : this.firstFrameUrl.slice(queryIndex);
        var directory = base.slice(0, base.lastIndexOf('/') + 1);
        return directory + encodeURIComponent(filename) + query;
      }

      initializeVideo() {
        var video = document.createElement('video');
        this.video = video;
        video.muted = true;
        video.playsInline = true;
        video.preload = 'auto';
        video.crossOrigin = 'anonymous';

        video.addEventListener('loadedmetadata', () => {
          if (this.isDestroyed) return;
          this.videoDuration = Math.max(0, video.duration - 0.04);
          video.currentTime = 0;
        }, { once: true });

        video.addEventListener('loadeddata', () => {
          if (this.isDestroyed) return;
          this.drawVideoFrame(true);
          this.section.classList.add('is-ready');
          if (this.loader) this.loader.setAttribute('aria-hidden', 'true');
          if (!this.isReducedMotion) this.onScroll();
        }, { once: true });

        video.addEventListener('seeked', () => {
          if (this.isDestroyed) return;
          this.drawVideoFrame(true);
          if (!this.isReducedMotion && Math.abs(this.targetTime - video.currentTime) >= 0.015 && !this.animationFrame) {
            this.animationFrame = window.requestAnimationFrame(this.tick);
          }
        });

        video.addEventListener('error', () => this.showError(), { once: true });
        video.src = this.videoUrl;
        video.load();
      }

      preloadSequence() {
        this.loadFrame(0).finally(() => {
          if (this.isDestroyed || this.isReducedMotion) return;
          var queue = Array.from({ length: this.totalFrames - 1 }, (_, index) => index + 1);
          var workerCount = Math.min(8, queue.length);
          var cursor = 0;

          var worker = async () => {
            while (!this.isDestroyed && cursor < queue.length) {
              var index = queue[cursor++];
              await this.loadFrame(index);
            }
          };

          for (var workerIndex = 0; workerIndex < workerCount; workerIndex += 1) {
            worker();
          }
        });
      }

      loadFrame(index) {
        return new Promise((resolve) => {
          var image = new Image();
          image.decoding = 'async';
          if (index === 0) image.fetchPriority = 'high';

          image.onload = () => {
            if (this.isDestroyed) return resolve();
            this.frames[index] = image;
            this.loadedFrames.add(index);
            this.settledCount += 1;

            if (index === 0 || index === Math.round(this.targetFrame)) {
              this.drawFrame(index, true);
            }

            this.updateLoadingState();
            resolve();
          };

          image.onerror = () => {
            this.settledCount += 1;
            this.updateLoadingState();
            resolve();
          };

          image.src = this.makeFrameUrl(index + 1);
        });
      }

      updateLoadingState() {
        if (this.loaderText && !this.section.classList.contains('is-ready')) {
          var progress = Math.min(100, Math.round((this.loadedFrames.size / this.minimumReady) * 100));
          this.loaderText.textContent = 'Loading experience ' + progress + '%';
        }

        if (this.loadedFrames.size >= this.minimumReady) {
          this.section.classList.add('is-ready');
          if (this.loader) this.loader.setAttribute('aria-hidden', 'true');
          this.onScroll();
          return;
        }

        if (this.settledCount === this.totalFrames && this.loadedFrames.size === 0) {
          this.showError();
        }
      }

      showError() {
        this.section.classList.add('is-error');
        if (this.loaderText) this.loaderText.textContent = 'Hero media unavailable';
      }

      measure() {
        var rect = this.section.getBoundingClientRect();
        this.sectionTop = rect.top + window.scrollY;
        this.scrollDistance = Math.max(1, this.section.offsetHeight - window.innerHeight);
      }

      resizeCanvas() {
        var rect = this.canvas.getBoundingClientRect();
        if (!rect.width || !rect.height) return;

        var pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
        var width = Math.round(rect.width * pixelRatio);
        var height = Math.round(rect.height * pixelRatio);

        if (this.canvas.width !== width || this.canvas.height !== height) {
          this.canvas.width = width;
          this.canvas.height = height;
          this.context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
          this.canvasWidth = rect.width;
          this.canvasHeight = rect.height;
          this.drawnFrame = -1;
          this.drawnVideoTime = -1;
          if (this.sourceMode === 'video') {
            this.drawVideoFrame(true);
          } else {
            this.drawNearestLoadedFrame(Math.round(this.currentFrame), true);
          }
        }
      }

      drawFrame(index, force) {
        var image = this.frames[index];
        if (!image || !image.naturalWidth || (!force && index === this.drawnFrame)) return false;

        this.drawCover(image, image.naturalWidth, image.naturalHeight);
        this.drawnFrame = index;
        return true;
      }

      drawVideoFrame(force) {
        if (!this.video || this.video.readyState < 2 || !this.video.videoWidth) return false;
        if (!force && Math.abs(this.video.currentTime - this.drawnVideoTime) < 0.005) return false;

        this.drawCover(this.video, this.video.videoWidth, this.video.videoHeight);
        this.drawnVideoTime = this.video.currentTime;
        return true;
      }

      drawCover(media, mediaWidth, mediaHeight) {

        var canvasWidth = this.canvasWidth || this.canvas.clientWidth;
        var canvasHeight = this.canvasHeight || this.canvas.clientHeight;
        var imageRatio = mediaWidth / mediaHeight;
        var canvasRatio = canvasWidth / canvasHeight;
        var drawWidth;
        var drawHeight;
        var drawX;
        var drawY;

        if (imageRatio > canvasRatio) {
          drawHeight = canvasHeight;
          drawWidth = drawHeight * imageRatio;
          drawX = (canvasWidth - drawWidth) / 2;
          drawY = 0;
        } else {
          drawWidth = canvasWidth;
          drawHeight = drawWidth / imageRatio;
          drawX = 0;
          drawY = (canvasHeight - drawHeight) / 2;
        }

        this.context.clearRect(0, 0, canvasWidth, canvasHeight);
        this.context.drawImage(media, drawX, drawY, drawWidth, drawHeight);
      }

      drawNearestLoadedFrame(targetIndex, force) {
        if (this.drawFrame(targetIndex, force)) return;

        for (var distance = 1; distance < this.totalFrames; distance += 1) {
          var previous = targetIndex - distance;
          var next = targetIndex + distance;
          if (previous >= 0 && this.drawFrame(previous, force)) return;
          if (next < this.totalFrames && this.drawFrame(next, force)) return;
        }
      }

      updateTargetFrame() {
        var progress = (window.scrollY - this.sectionTop) / this.scrollDistance;
        progress = Math.min(1, Math.max(0, progress));
        if (this.sourceMode === 'video') {
          this.targetTime = progress * this.videoDuration;
        } else {
          this.targetFrame = progress * (this.totalFrames - 1);
        }
      }

      onScroll() {
        if (this.isDestroyed || this.isReducedMotion || !this.isActive || this.scrollFrame) return;

        this.scrollFrame = window.requestAnimationFrame(() => {
          this.scrollFrame = 0;
          this.updateTargetFrame();
          if (!this.animationFrame) this.animationFrame = window.requestAnimationFrame(this.tick);
        });
      }

      tick() {
        this.animationFrame = 0;
        if (this.isDestroyed) return;

        if (this.sourceMode === 'video') {
          this.tickVideo();
          return;
        }

        var difference = this.targetFrame - this.currentFrame;
        this.currentFrame += difference * 0.2;

        if (Math.abs(difference) < 0.02) {
          this.currentFrame = this.targetFrame;
        }

        this.drawNearestLoadedFrame(Math.round(this.currentFrame), false);

        if (Math.abs(this.targetFrame - this.currentFrame) >= 0.02) {
          this.animationFrame = window.requestAnimationFrame(this.tick);
        }
      }

      tickVideo() {
        if (!this.video || !this.videoDuration || this.video.seeking) return;

        var difference = this.targetTime - this.video.currentTime;
        if (Math.abs(difference) < 0.015) {
          this.drawVideoFrame(false);
          return;
        }

        var nextTime = this.video.currentTime + (difference * 0.32);
        this.video.currentTime = Math.min(this.videoDuration, Math.max(0, nextTime));
      }

      onResize() {
        if (this.isDestroyed) return;
        window.cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = window.requestAnimationFrame(() => {
          this.resizeCanvas();
          this.measure();
          this.updateTargetFrame();
          if (this.sourceMode === 'video') {
            this.drawVideoFrame(true);
          } else {
            this.drawNearestLoadedFrame(Math.round(this.currentFrame), true);
          }
        });
      }

      destroy() {
        this.isDestroyed = true;
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onResize);
        window.cancelAnimationFrame(this.animationFrame);
        window.cancelAnimationFrame(this.scrollFrame);
        window.cancelAnimationFrame(this.resizeFrame);
        if (this.resizeObserver) this.resizeObserver.disconnect();
        if (this.visibilityObserver) this.visibilityObserver.disconnect();
        if (this.video) {
          this.video.removeAttribute('src');
          this.video.load();
          this.video = null;
        }
        this.frames.length = 0;
        this.loadedFrames.clear();
        delete this.section.dataset.heroScrollInitialized;
      }
    }

    window[GLOBAL_KEY] = {
      Controller: HeroScrollSequence,
      instances: new Map(),
      listenersBound: false
    };
  }

  var registry = window[GLOBAL_KEY];

  function initialize(scope) {
    var root = scope || document;
    var sections = [];

    if (root.matches && root.matches('[data-hero-scroll]')) sections.push(root);
    if (root.querySelectorAll) {
      root.querySelectorAll('[data-hero-scroll]').forEach(function (section) {
        sections.push(section);
      });
    }

    sections.forEach(function (section) {
      if (section.dataset.heroScrollInitialized === 'true') return;
      section.dataset.heroScrollInitialized = 'true';
      registry.instances.set(section.id, new registry.Controller(section));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initialize(document); }, { once: true });
  } else {
    initialize(document);
  }

  if (!registry.listenersBound && window.Shopify && Shopify.designMode) {
    registry.listenersBound = true;

    document.addEventListener('shopify:section:load', function (event) {
      initialize(event.target);
    });

    document.addEventListener('shopify:section:unload', function (event) {
      var section = event.target.querySelector('[data-hero-scroll]');
      if (!section) return;
      var instance = registry.instances.get(section.id);
      if (instance) instance.destroy();
      registry.instances.delete(section.id);
    });
  }
})();